import React, { useRef, useState, useEffect, useCallback } from "react";
import QRCodeStyling from "qr-code-styling";
import { db } from "../firebase";
import {
  getDocs, query, collection, orderBy,
  doc, updateDoc, deleteDoc, setDoc, serverTimestamp,
} from "firebase/firestore";
import { Logo, ConfirmDialog, GlobalStyles, BtnPrimary, BtnGhost } from "../components/UI";
import { Toast, useToast } from "../hooks/useToast";
import {
  ACCENT, ACCENT2, BORDER, MUTED,
  STATUS_FLOW, STATUS_META, getStatusMeta,
  CARD_COLORS, DEFAULT_PRICE, PRICE_DOC_PATH,
} from "../utils/constants";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "../firebase";
import AdminLogin from "./AdminLogin";

/* ── Edit Modal ─────────────────────────── */
const EDIT_FIELDS = [
  { key: "name", label: "Full Name", span: 2, placeholder: "Full name" },
  { key: "dob", label: "Date of Birth", span: 1, type: "date" },
  { key: "gender", label: "Gender", span: 1, placeholder: "e.g. Male / Female" },
  { key: "bloodGroup", label: "Blood Group", span: 1, placeholder: "e.g. B+" },
  { key: "diabetes", label: "Diabetes", span: 1, placeholder: "e.g. Type 2 / None" },
  { key: "bloodPressure", label: "Blood Pressure", span: 1, placeholder: "e.g. 120/80" },
  { key: "phone", label: "Phone", span: 1, placeholder: "Phone number" },
  { key: "address", label: "Residential Address", span: 2, placeholder: "Home address" },
  { key: "shippingAddress", label: "Shipping Address", span: 2, placeholder: "Full shipping address" },
  { key: "pincode", label: "Pincode", span: 1, placeholder: "Pincode" },
];

function EditModal({ order, onClose, onSave }) {
  const [form, setForm] = useState({
    name: order.name || "", dob: order.dob || "",
    gender: order.gender || "", address: order.address || "",
    shippingAddress: order.shippingAddress || "", pincode: order.pincode || "",
    phone: order.phone || "", bloodGroup: order.bloodGroup || "",
    diabetes: order.diabetes || "", bloodPressure: order.bloodPressure || "",
    cardColor: order.cardColor || "lavender",
    emergencyContacts: (() => {
      const base = order.emergencyContacts || [];
      const p = [...base];
      while (p.length < 3) p.push({ name: "", phone: "" });
      return p.slice(0, 3);
    })(),
  });
  const [saving, setSaving] = useState(false);
  const inp = { width: "100%", padding: "9px 12px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", fontSize: 13, fontFamily: "DM Sans,sans-serif", outline: "none", boxSizing: "border-box", colorScheme: "dark" };

  const handleSave = async () => {
    if (!form.name.trim()) { alert("Name is required"); return; }
    setSaving(true);
    await onSave({ ...form, name: form.name.trim(), emergencyContacts: form.emergencyContacts.filter(c => c.name?.trim()) });
    setSaving(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 7000, background: "rgba(0,0,0,0.8)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, overflowY: "auto" }}>
      <div style={{ background: "#0d1225", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 20, width: "100%", maxWidth: 560, maxHeight: "92vh", overflowY: "auto", padding: 24 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div style={{ fontFamily: "Syne,sans-serif", fontSize: 17, fontWeight: 700, color: "#fff" }}>Edit Profile</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: MUTED, fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9, marginBottom: 14 }}>
          {EDIT_FIELDS.map(f => (
            <div key={f.key} style={{ gridColumn: `span ${f.span}` }}>
              <label style={{ fontSize: 11, color: "#a89fff", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 4 }}>{f.label}</label>
              <input type={f.type || "text"} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder || ""} style={inp} />
            </div>
          ))}
        </div>
        {/* Color */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 11, color: "#a89fff", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 7 }}>Card Colour</label>
          <div style={{ display: "flex", gap: 8 }}>
            {CARD_COLORS.map(c => (
              <button key={c.id} type="button" title={c.label} onClick={() => setForm(p => ({ ...p, cardColor: c.id }))}
                style={{ width: 24, height: 24, borderRadius: "50%", background: c.hex, border: "none", cursor: "pointer", boxShadow: form.cardColor === c.id ? `0 0 0 2px #fff,0 0 0 4px ${c.hex}` : "0 0 0 1px rgba(255,255,255,0.2)" }} />
            ))}
          </div>
        </div>
        {/* Emergency contacts */}
        <div style={{ fontSize: 11, color: "#a89fff", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.07)" }}>Emergency Contacts</div>
        {form.emergencyContacts.map((ec, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 7 }}>
            <input value={ec.name} onChange={e => { const a = [...form.emergencyContacts]; a[i] = { ...a[i], name: e.target.value }; setForm(p => ({ ...p, emergencyContacts: a })); }} placeholder={`Contact ${i + 1} name`} style={inp} />
            <input value={ec.phone} onChange={e => { const a = [...form.emergencyContacts]; a[i] = { ...a[i], phone: e.target.value }; setForm(p => ({ ...p, emergencyContacts: a })); }} placeholder={`Contact ${i + 1} phone`} style={inp} />
          </div>
        ))}
        <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "11px", borderRadius: 10, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", cursor: "pointer", fontFamily: "DM Sans,sans-serif", fontSize: 14 }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ flex: 1, padding: "11px", borderRadius: 10, background: `linear-gradient(135deg,${ACCENT},${ACCENT2})`, border: "none", color: "#fff", cursor: "pointer", fontFamily: "DM Sans,sans-serif", fontSize: 14, fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
      <style>{`input::placeholder{color:rgba(240,238,248,0.28);}input:focus{border-color:rgba(124,111,255,0.5)!important;outline:none;}`}</style>
    </div>
  );
}

/* ── ADMIN PAGE ─────────────────────────── */
export default function AdminPage({ onPriceChange }) {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [tab, setTab] = useState("active");
  const [search, setSearch] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [trackingId, setTrackingId] = useState("");
  const [testMode, setTestMode] = useState(false);
  const [price, setPrice] = useState(DEFAULT_PRICE);
  const [editingPrice, setEditingPrice] = useState(false);
  const [priceInput, setPriceInput] = useState(String(DEFAULT_PRICE));
  const { toast, show: showToast, hide: hideToast } = useToast();
  const qrRef = useRef(null);
  const qrInstance = useRef(null);

  useEffect(() => { document.title = "Admin — DialHoop"; fetchOrders(); }, []);
  // Inside AdminPage component, add these state vars:
const [authed, setAuthed] = useState(false);
const [authChecking, setAuthChecking] = useState(true);
  // Add this useEffect (before your existing fetchOrders one):
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setAuthed(!!user);
      setAuthChecking(false);
    });
    return () => unsub();
  }, []);

  // Add a sign out handler:
  const handleSignOut = async () => {
    await signOut(auth);
    setAuthed(false);
  };

  const fetchOrders = async () => {
    setLoadingOrders(true);
    try {
      const q = query(collection(db, "contacts"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch { showToast("Failed to load orders", "error"); }
    setLoadingOrders(false);
  };

  const renderQR = useCallback((url) => {
    if (!qrRef.current) return;
    qrRef.current.innerHTML = "";
    qrInstance.current = new QRCodeStyling({
      width: 220, height: 220, data: url,
      dotsOptions: { color: "#111", type: "rounded" },
      cornersSquareOptions: { type: "extra-rounded" },
      backgroundOptions: { color: "transparent" },
    });
    qrInstance.current.append(qrRef.current);
  }, []);

  const selectOrder = (order) => {
    setSelected(order);
    setGeneratedUrl(order.qrUrl || "");
    setTrackingId(order.trackingId || "");
    qrInstance.current = null;
    if (qrRef.current) qrRef.current.innerHTML = "";
    if (order.qrUrl) setTimeout(() => renderQR(order.qrUrl), 80);
  };

  const handleGenerateQR = async () => {
    if (!selected) return;
    setGenerating(true);
    try {
      const url = `https://dialhoop.com/profile/${selected.id}`;
      await updateDoc(doc(db, "contacts", selected.id), { status: "qr_generated", qrUrl: url, updatedAt: serverTimestamp() });
      const updated = { ...selected, status: "qr_generated", qrUrl: url };
      setOrders(p => p.map(o => o.id === selected.id ? updated : o));
      setSelected(updated); setGeneratedUrl(url);
      setTimeout(() => renderQR(url), 80);
      showToast("QR generated — status → QR Done");
    } catch { showToast("Failed to generate QR", "error"); }
    setGenerating(false);
  };

  const handleDownload = async () => {
    if (!qrInstance.current) return;
    await qrInstance.current.download({ name: `DialHoop-${selected?.name || "QR"}`, extension: "png" });
    showToast("QR downloaded");
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      const update = { status: newStatus, updatedAt: serverTimestamp() };
      if (newStatus === "shipped" && trackingId.trim()) update.trackingId = trackingId.trim();
      await updateDoc(doc(db, "contacts", selected.id), update);
      const updated = { ...selected, ...update };
      setOrders(p => p.map(o => o.id === selected.id ? updated : o));
      setSelected(updated);
      showToast(`Status → ${getStatusMeta(newStatus).label}`);
    } catch { showToast("Failed to update status", "error"); }
  };

  const handleTestPayment = async () => {
    if (!selected) return;
    const fakeId = `TEST_${Date.now()}`;
    try {
      await updateDoc(doc(db, "contacts", selected.id), { status: "paid", paymentId: fakeId, paymentMode: "test", amount: price, updatedAt: serverTimestamp() });
      const updated = { ...selected, status: "paid", paymentId: fakeId, paymentMode: "test", amount: price };
      setOrders(p => p.map(o => o.id === selected.id ? updated : o));
      setSelected(updated);
      showToast(`Test payment simulated — ${fakeId}`, "warn");
    } catch { showToast("Test payment failed", "error"); }
  };

  const handleSaveEdit = async (fields) => {
    try {
      await updateDoc(doc(db, "contacts", selected.id), { ...fields, updatedAt: serverTimestamp() });
      const updated = { ...selected, ...fields };
      setOrders(p => p.map(o => o.id === selected.id ? updated : o));
      setSelected(updated); setEditOpen(false);
      showToast("Profile updated");
    } catch { showToast("Failed to update profile", "error"); }
  };

  const handleDelete = async () => {
    try {
      await deleteDoc(doc(db, "contacts", selected.id));
      setOrders(p => p.filter(o => o.id !== selected.id));
      setSelected(null); setGeneratedUrl(""); setConfirmDelete(false);
      showToast("Profile deleted", "warn");
    } catch { showToast("Failed to delete", "error"); }
  };

  /* ── Price save → writes to Firestore + notifies App.jsx ── */
  const handlePriceSave = async () => {
    const n = parseInt(priceInput, 10);
    if (!n || n < 1) { showToast("Enter a valid price", "error"); return; }
    try {
      await setDoc(
        doc(db, PRICE_DOC_PATH.col, PRICE_DOC_PATH.docId),
        { [PRICE_DOC_PATH.field]: n },
        { merge: true }
      );
      setPrice(n);
      setEditingPrice(false);
      if (onPriceChange) onPriceChange(n);   // ← updates Landing + OrderPage live
      showToast(`Price updated to ₹${n} — live everywhere now ✓`);
    } catch { showToast("Failed to save price", "error"); }
  };

  const active = orders.filter(o => o.status !== "delivered");
  const delivered = orders.filter(o => o.status === "delivered");
  const base = tab === "active" ? active : delivered;
  const displayed = search.trim()
    ? base.filter(o => o.name?.toLowerCase().includes(search.toLowerCase()) || o.memberId?.toLowerCase().includes(search.toLowerCase()) || o.pincode?.includes(search))
    : base;

  const aInp = { width: "100%", padding: "9px 12px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", fontSize: 13, fontFamily: "DM Sans,sans-serif", outline: "none", boxSizing: "border-box" };
  if (authChecking) {
    return (
      <div style={{ minHeight: "100vh", background: "#080B18", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ color: "rgba(240,238,248,0.4)", fontFamily: "DM Sans, sans-serif", fontSize: 14 }}>Checking auth…</div>
      </div>
    );
  }

  if (!authed) {
    return <AdminLogin onLogin={() => setAuthed(true)} />;
  }
  return (
    <div style={{ minHeight: "100vh", background: "#080B18", color: "#F0EEF8", fontFamily: "DM Sans, sans-serif", display: "flex", flexDirection: "column" }}>
      <div style={{ position: "fixed", top: "-20%", left: "30%", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle,rgba(124,111,255,0.07) 0%,transparent 70%)", pointerEvents: "none", zIndex: 0 }} />

      {/* Nav */}
      <nav style={{ position: "relative", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 20px", background: "rgba(8,11,24,0.9)", backdropFilter: "blur(24px)", borderBottom: `1px solid ${BORDER}`, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Logo size={40} />
          <span style={{ fontFamily: "Syne,sans-serif", fontWeight: 700, fontSize: 18, color: "#fff" }}>DialHoop</span>
          <span style={{ fontSize: 10, fontWeight: 600, background: "rgba(124,111,255,0.2)", color: "#a89fff", padding: "3px 9px", borderRadius: 100, letterSpacing: "0.5px", textTransform: "uppercase" }}>Admin</span>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {/* Live price editor */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {editingPrice ? (
              <>
                <span style={{ fontSize: 12, color: MUTED }}>₹</span>
                <input value={priceInput} onChange={e => setPriceInput(e.target.value)} onKeyDown={e => e.key === "Enter" && handlePriceSave()} style={{ ...aInp, width: 68, padding: "5px 8px", fontSize: 13 }} autoFocus />
                <button onClick={handlePriceSave} style={{ padding: "5px 10px", borderRadius: 7, background: `linear-gradient(135deg,${ACCENT},${ACCENT2})`, border: "none", color: "#fff", cursor: "pointer", fontSize: 12 }}>Save</button>
                <button onClick={() => setEditingPrice(false)} style={{ padding: "5px 8px", borderRadius: 7, background: "transparent", border: `1px solid ${BORDER}`, color: MUTED, cursor: "pointer", fontSize: 12 }}>✕</button>
              </>
            ) : (
              <button onClick={() => { setEditingPrice(true); setPriceInput(String(price)); }} style={{ padding: "5px 12px", borderRadius: 8, background: "rgba(255,255,255,0.05)", border: `1px solid ${BORDER}`, color: "#fff", cursor: "pointer", fontFamily: "DM Sans,sans-serif", fontSize: 12, display: "flex", alignItems: "center", gap: 5 }}>
                <span>₹{price}</span><span style={{ fontSize: 10 }}>✏️</span>
              </button>
            )}
          </div>
          <span style={{ fontSize: 11, color: "rgba(240,238,248,0.3)" }}>{active.length} active · {delivered.length} done</span>
          <button onClick={() => setTestMode(p => !p)} style={{ padding: "5px 10px", borderRadius: 7, border: `1px solid ${testMode ? "rgba(239,159,39,0.5)" : BORDER}`, background: testMode ? "rgba(239,159,39,0.1)" : "transparent", color: testMode ? "#f5c460" : MUTED, fontFamily: "DM Sans,sans-serif", fontSize: 11, cursor: "pointer" }}>
            ⚗ {testMode ? "Test ON" : "Test"}
          </button>
          <button
            onClick={handleSignOut}
            style={{ padding: "5px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, background: "transparent", color: MUTED, cursor: "pointer", fontFamily: "DM Sans,sans-serif", fontSize: 12 }}
          >
            Sign out
          </button>
          <button style={{ ...aInp, width: "auto", padding: "5px 12px", cursor: "pointer", fontSize: 12 }} onClick={fetchOrders}>↻ Refresh</button>
        </div>
      </nav>

      <div style={{ display: "flex", flex: 1, position: "relative", zIndex: 1, overflow: "hidden", minHeight: "calc(100vh - 64px)" }}>

        {/* LEFT LIST */}
        <div style={{ width: 280, minWidth: 200, borderRight: `1px solid ${BORDER}`, display: "flex", flexDirection: "column", flexShrink: 0 }}>
          <div style={{ padding: "10px 10px 6px" }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, ID, pincode…" style={{ ...aInp, width: "100%" }} />
          </div>
          <div style={{ display: "flex", gap: 5, padding: "4px 10px 8px" }}>
            {["active", "delivered"].map(t => (
              <button key={t} onClick={() => setTab(t)} style={{ flex: 1, padding: "6px 4px", borderRadius: 7, border: "none", background: tab === t ? "rgba(124,111,255,0.2)" : "rgba(255,255,255,0.04)", color: tab === t ? "#a89fff" : "rgba(240,238,248,0.35)", fontFamily: "DM Sans,sans-serif", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                {t === "active" ? `📋 Active (${active.length})` : `📦 Done (${delivered.length})`}
              </button>
            ))}
          </div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            {loadingOrders
              ? <div style={{ padding: 18, color: "rgba(240,238,248,0.3)", fontSize: 13, textAlign: "center" }}>Loading…</div>
              : displayed.length === 0
                ? <div style={{ padding: 18, color: "rgba(240,238,248,0.3)", fontSize: 13, textAlign: "center" }}>No orders found.</div>
                : displayed.map(order => {
                  const m = getStatusMeta(order.status);
                  const clr = CARD_COLORS.find(c => c.id === order.cardColor);
                  return (
                    <div key={order.id} onClick={() => selectOrder(order)} style={{ padding: "10px", borderBottom: "1px solid rgba(255,255,255,0.04)", cursor: "pointer", background: selected?.id === order.id ? "rgba(124,111,255,0.1)" : "transparent", borderLeft: selected?.id === order.id ? "3px solid #7C6FFF" : "3px solid transparent", transition: "background .12s" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 30, height: 30, borderRadius: "50%", background: clr ? clr.hex : `linear-gradient(135deg,${ACCENT},${ACCENT2})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#333", flexShrink: 0, overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)" }}>
                          {order.photo ? <img src={order.photo} alt="" style={{ width: 30, height: 30, objectFit: "cover" }} /> : (order.name || "?").charAt(0).toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500, fontSize: 12, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{order.name}</div>
                          <div style={{ fontSize: 10, color: "rgba(240,238,248,0.3)", marginTop: 1 }}>{order.memberId} · {order.pincode || "—"}</div>
                        </div>
                        <span style={{ fontSize: 9, padding: "2px 5px", borderRadius: 100, background: m.bg, color: m.color, fontWeight: 600, flexShrink: 0 }}>{order.status || "pend"}</span>
                      </div>
                    </div>
                  );
                })
            }
          </div>
        </div>

        {/* RIGHT DETAIL */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {!selected ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60%", color: "rgba(240,238,248,0.2)", textAlign: "center" }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>👈</div>
              <div style={{ fontSize: 14 }}>Select an order</div>
            </div>
          ) : (
            <div style={{ maxWidth: 680 }}>
              {/* Header */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                {(() => {
                  const clr = CARD_COLORS.find(c => c.id === selected.cardColor);
                  return (
                    <div style={{ width: 46, height: 46, borderRadius: "50%", background: clr ? clr.hex : `linear-gradient(135deg,${ACCENT},${ACCENT2})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700, color: "#333", overflow: "hidden", flexShrink: 0, border: "1px solid rgba(255,255,255,0.12)" }}>
                      {selected.photo ? <img src={selected.photo} alt="" style={{ width: 46, height: 46, objectFit: "cover" }} /> : selected.name.charAt(0).toUpperCase()}
                    </div>
                  );
                })()}
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "Syne,sans-serif", fontSize: 18, fontWeight: 700, color: "#fff" }}>{selected.name}</div>
                  <div style={{ fontSize: 10, color: "rgba(240,238,248,0.3)", marginTop: 1, fontFamily: "monospace" }}>{selected.memberId}</div>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                  {(() => { const m = getStatusMeta(selected.status); return <span style={{ fontSize: 11, padding: "3px 9px", borderRadius: 100, background: m.bg, color: m.color, fontWeight: 600 }}>{m.label}</span>; })()}
                  <button onClick={() => setEditOpen(true)} style={{ padding: "5px 11px", borderRadius: 7, background: "rgba(124,111,255,0.1)", border: "1px solid rgba(124,111,255,0.2)", color: "#a89fff", cursor: "pointer", fontFamily: "DM Sans,sans-serif", fontSize: 11, fontWeight: 500 }}>✏️ Edit</button>
                  <button onClick={() => setConfirmDelete(true)} style={{ padding: "5px 11px", borderRadius: 7, background: "rgba(231,76,60,0.08)", border: "1px solid rgba(231,76,60,0.2)", color: "#e74c3c", cursor: "pointer", fontFamily: "DM Sans,sans-serif", fontSize: 11, fontWeight: 500 }}>🗑 Delete</button>
                </div>
              </div>

              {/* Payment badge */}
              {selected.paymentId && (
                <div style={{ background: "rgba(99,153,34,0.07)", border: "1px solid rgba(99,153,34,0.2)", borderRadius: 9, padding: "8px 13px", marginBottom: 12, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, color: "#9fd45a" }}>💳 Payment confirmed</span>
                  <span style={{ fontSize: 10, color: "rgba(240,238,248,0.35)", fontFamily: "monospace" }}>{selected.paymentId}</span>
                  {selected.paymentMode === "test" && <span style={{ fontSize: 9, padding: "1px 7px", borderRadius: 100, background: "rgba(239,159,39,0.2)", color: "#f5c460" }}>TEST</span>}
                  <span style={{ marginLeft: "auto", fontSize: 13, color: "#fff", fontWeight: 700 }}>₹{selected.amount || price}</span>
                </div>
              )}

              {/* Card colour */}
              {(() => {
                const c = CARD_COLORS.find(x => x.id === selected.cardColor); return c ? (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
                    <div style={{ width: 14, height: 14, borderRadius: "50%", background: c.hex, border: "1px solid rgba(255,255,255,0.15)" }} />
                    <span style={{ fontSize: 12, color: MUTED }}>{c.label} card</span>
                  </div>
                ) : null;
              })()}

              {/* Info grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginBottom: 10 }}>
                {[["Blood Group", selected.bloodGroup], ["DOB", selected.dob], ["Gender", selected.gender], ["Blood Pressure", selected.bloodPressure], ["Diabetes", selected.diabetes], ["Phone", selected.phone], ["Submitted", selected.createdAt?.toDate?.()?.toLocaleDateString("en-IN") || "—"]].map(([l, v]) => (
                  <div key={l} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 9, padding: "10px 12px" }}>
                    <div style={{ fontSize: 9, color: "rgba(240,238,248,0.3)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>{l}</div>
                    <div style={{ fontSize: 12, color: "#fff", fontWeight: 500 }}>{v || "—"}</div>
                  </div>
                ))}
              </div>

              {/* Addresses */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
                {[["🏠 Home", selected.address], ["📦 Ship to", `${selected.shippingAddress || "—"}${selected.pincode ? ` · ${selected.pincode}` : ""}`]].map(([l, v]) => (
                  <div key={l} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 9, padding: "10px 12px" }}>
                    <div style={{ fontSize: 9, color: "rgba(240,238,248,0.3)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>{l}</div>
                    <div style={{ fontSize: 12, color: "#fff" }}>{v || "—"}</div>
                  </div>
                ))}
              </div>

              {/* Emergency contacts */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: "#a89fff", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 7 }}>Emergency Contacts</div>
                {(selected.emergencyContacts || []).filter(c => c.name).map((c, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(124,111,255,0.05)", border: "1px solid rgba(124,111,255,0.1)", borderRadius: 8, padding: "8px 12px", marginBottom: 5 }}>
                    <span style={{ fontSize: 12 }}>📞</span>
                    <span style={{ fontSize: 12, color: "#fff", fontWeight: 500 }}>{c.name}</span>
                    <span style={{ marginLeft: "auto", fontSize: 11, color: MUTED, fontFamily: "monospace" }}>{c.phone}</span>
                  </div>
                ))}
                {!(selected.emergencyContacts || []).filter(c => c.name).length && <div style={{ fontSize: 12, color: "rgba(240,238,248,0.3)" }}>No contacts added</div>}
              </div>

              {/* Status pipeline */}
              <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 16, marginBottom: 10 }}>
                <div style={{ fontFamily: "Syne,sans-serif", fontSize: 12, fontWeight: 600, color: "#fff", marginBottom: 9 }}>Status Pipeline</div>
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                  {STATUS_FLOW.map(st => {
                    const m = STATUS_META[st];
                    const active = selected.status === st;
                    return <button key={st} onClick={() => handleStatusUpdate(st)} style={{ padding: "5px 10px", borderRadius: 7, border: `1px solid ${active ? m.color : "rgba(255,255,255,0.08)"}`, background: active ? m.bg : "transparent", color: active ? m.color : "rgba(240,238,248,0.35)", cursor: "pointer", fontFamily: "DM Sans,sans-serif", fontSize: 11, fontWeight: active ? 600 : 400 }}>{m.label}</button>;
                  })}
                </div>
                <div style={{ display: "flex", gap: 7, marginTop: 9 }}>
                  <input value={trackingId} onChange={e => setTrackingId(e.target.value)} placeholder="Paste tracking ID, then click Mark Shipped" style={{ ...aInp, flex: 1, fontSize: 12 }} />
                  {trackingId.trim() && <button onClick={() => handleStatusUpdate("shipped")} style={{ padding: "6px 11px", borderRadius: 7, background: "rgba(124,111,255,0.15)", border: "1px solid rgba(124,111,255,0.3)", color: "#a89fff", cursor: "pointer", fontSize: 11 }}>🚚 Shipped</button>}
                </div>
                {selected.trackingId && <div style={{ fontSize: 11, color: MUTED, marginTop: 5 }}>Tracking: <span style={{ fontFamily: "monospace", color: "#a89fff" }}>{selected.trackingId}</span></div>}
              </div>

              {/* Test payment */}
              {testMode && (
                <div style={{ background: "rgba(239,159,39,0.05)", border: "1px solid rgba(239,159,39,0.2)", borderRadius: 12, padding: 14, marginBottom: 10 }}>
                  <div style={{ fontFamily: "Syne,sans-serif", fontSize: 12, fontWeight: 600, color: "#f5c460", marginBottom: 7 }}>⚗ Test Payment Simulator</div>
                  <div style={{ fontSize: 11, color: MUTED, marginBottom: 9 }}>Simulates ₹{price} payment without real money.</div>
                  <button onClick={handleTestPayment} style={{ padding: "7px 14px", borderRadius: 7, background: "rgba(239,159,39,0.14)", border: "1px solid rgba(239,159,39,0.3)", color: "#f5c460", cursor: "pointer", fontFamily: "DM Sans,sans-serif", fontSize: 12, fontWeight: 500 }}>
                    💳 Simulate ₹{price} Payment
                  </button>
                </div>
              )}

              {/* QR generator */}
              <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 18 }}>
                <div style={{ fontFamily: "Syne,sans-serif", fontSize: 12, fontWeight: 600, color: "#fff", marginBottom: 11 }}>QR Card Generator</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: generatedUrl ? 14 : 0 }}>
                  <BtnPrimary onClick={handleGenerateQR} disabled={generating} style={{ padding: "9px 16px", fontSize: 13, opacity: generating ? 0.7 : 1 }}>
                    {generating ? "Generating…" : generatedUrl ? "↻ Regenerate" : "⚡ Generate QR"}
                  </BtnPrimary>
                  {generatedUrl && <BtnGhost onClick={handleDownload} style={{ padding: "9px 16px", fontSize: 13 }}>↓ Download PNG</BtnGhost>}
                </div>
                {generatedUrl && (
                  <>
                    <div style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.07)", padding: "7px 11px", borderRadius: 7, fontSize: 11, fontFamily: "monospace", wordBreak: "break-all", color: "#a89fff", marginBottom: 14 }}>{generatedUrl}</div>
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <div ref={qrRef} style={{ background: "#fff", borderRadius: 10, padding: 12, display: "inline-block" }} />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {editOpen && selected && <EditModal order={selected} onClose={() => setEditOpen(false)} onSave={handleSaveEdit} />}
      {confirmDelete && <ConfirmDialog title="Delete this profile?" body={`Permanently delete ${selected?.name}'s profile. Cannot be undone.`} onConfirm={handleDelete} onCancel={() => setConfirmDelete(false)} />}
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={hideToast} />}
      <GlobalStyles />
      <style>{`input::placeholder{color:rgba(240,238,248,0.28);}input:focus{border-color:rgba(124,111,255,0.5)!important;outline:none;}::-webkit-scrollbar{width:4px;height:4px;}::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.1);border-radius:4px;}`}</style>
    </div>
  );
}